import { useGetDayCountConventionsQuery } from '@src/components/OTC/externalRefDataApi';
import { LoanQuoteTerminationTypeEnum, PrepaymentOptionEnum } from '@talos/kyoko';
import { useMemo } from 'react';
import {
  getAllowedLoanPaymentCcyAssets,
  getLoanPaymentCurrencyOptionsForRow,
  isAllowedLoanPaymentCcy,
} from './loanPaymentCurrencyForRow';

export type LoanPaymentPeriodOption = { displayName: string; value: '1D' | '1W' | '1M' };
export type LoanTermTypeOption = { displayName: string; value: LoanQuoteTerminationTypeEnum };
export type LoanPaymentCurrencyOption = { value: string; displayName: string };

export { getAllowedLoanPaymentCcyAssets, getLoanPaymentCurrencyOptionsForRow, isAllowedLoanPaymentCcy };

export const LOAN_PAYMENT_PERIOD_OPTIONS = [
  { displayName: 'Daily', value: '1D' },
  { displayName: 'Weekly', value: '1W' },
  { displayName: 'Monthly', value: '1M' },
] as const satisfies Array<LoanPaymentPeriodOption>;

export const LOAN_TERM_TYPE_OPTIONS = [
  { displayName: 'Fixed', value: LoanQuoteTerminationTypeEnum.Fixed },
  { displayName: 'Open', value: LoanQuoteTerminationTypeEnum.OpenTerm },
] as const satisfies Array<LoanTermTypeOption>;

export type DayCountConventionOption = { displayName: string; value: string };
export type RateSourceOption = { displayName: string; value: string; description?: string };
export type PrepaymentOptionOption = { displayName: string; value: PrepaymentOptionEnum };

/**
 * Hook to retrieve day count convention options from the Reference Data Service
 * Spec: "Drop-down to be provided by the Reference Data Service: 30/360, Actual/360, Actual/365, Actual/Actual"
 */
export const useDayCountConventionOptions = (): DayCountConventionOption[] => {
  const { data: dayCountConventions } = useGetDayCountConventionsQuery();

  return useMemo(() => {
    return (
      dayCountConventions?.map(convention => ({
        displayName: convention.DisplayName,
        value: convention.Code,
      })) ?? []
    );
  }, [dayCountConventions]);
};

export const PREPAYMENT_OPTION_OPTIONS: PrepaymentOptionOption[] = [
  { displayName: 'Full', value: PrepaymentOptionEnum.Full },
  { displayName: 'Partial / Full', value: PrepaymentOptionEnum.Partial },
  { displayName: 'None', value: PrepaymentOptionEnum.None },
];

// Pure label getter functions
export const getLoanPaymentCurrencyLabel = (option: LoanPaymentCurrencyOption): string => option.displayName;
export const getTermTypeLabel = (option: LoanTermTypeOption): string => option.displayName;
export const getPaymentPeriodLabel = (option: LoanPaymentPeriodOption): string => option.displayName;
export const getDayCountConventionLabel = (option: DayCountConventionOption): string => option.displayName;
export const getRateSourceLabel = (option: RateSourceOption): string => option.displayName;
export const getPrepaymentOptionLabel = (option: PrepaymentOptionOption): string => option.displayName;

// MVP-only restriction
export type LoanCollateralAssetOption = { value: string; label: string };
